import { useStateChange } from '@aigc/hooks';
import { Image, type ImageProps } from 'antd';
import { useMemo, useState } from 'react';
import { match, P } from 'ts-pattern';
import { processOssImage } from './image-process';

interface OssImageProps extends Omit<ImageProps, 'width' | 'height'> {
  size?: number;

  mode?: 'lfit' | 'mfit' | 'fill' | 'pad' | 'fixed';
}

interface SrcState {
  originalSrc?: string;
  type: 'aliyun' | 'original';
  aliyunSrc?: string;
}

function getSrcState(
  originalSrc: OssImageProps['src'],
  size: OssImageProps['size'],
  mode: OssImageProps['mode'],
): SrcState {
  const aliyunSrc = processOssImage(originalSrc, size, mode ?? 'fill');

  if (aliyunSrc) {
    return {
      originalSrc,
      type: 'aliyun',
      aliyunSrc,
    };
  } else {
    return {
      originalSrc,
      type: 'original',
    };
  }
}

export const AliOssImage = (props: OssImageProps) => {
  const { size, src: originalSrc, mode, preview, ...imageProps } = props;
  const [state, setState] = useState(() =>
    getSrcState(originalSrc, size, mode),
  );

  useStateChange([originalSrc, size, mode], () => {
    setState(getSrcState(originalSrc, size, mode));
  });

  const src = useMemo(() => {
    switch (state?.type) {
      case 'aliyun':
        return state.aliyunSrc;
      case 'original':
        return state.originalSrc;
      default:
        return;
    }
  }, [state]);

  const _preview: ImageProps['preview'] = useMemo(() => {
    if (preview === false) {
      return false;
    }

    return preview === true || preview == null
      ? { src: originalSrc }
      : {
          src: originalSrc,
          ...preview,
        };
  }, [preview, originalSrc]);

  return (
    <Image
      {...imageProps}
      src={src}
      width={props.size}
      height={props.size}
      preview={_preview}
      classNames={{
        image: match(props.mode)
          .with('lfit', () => 'object-contain')
          .with(P.union('mfit', 'fill'), () => 'object-cover')
          .otherwise(() => void 0),
      }}
      onError={(e) => {
        switch (state?.type) {
          case 'aliyun':
            setState((prev) => ({
              ...prev,
              type: 'original',
            }));
            break;
          case 'original':
            imageProps.onError?.(e);
            break;
          default:
            break;
        }
      }}
    ></Image>
  );
};
